import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Package, TrendingUp, IndianRupee, Save } from 'lucide-react';
import { API } from '../config';
import { devLog } from '../utils/devLog';
import { DateStepper } from './ui/date-stepper';
import ExcelImportPanel from './ExcelImportPanel';

const DataSection = ({ title, icon, children }) => (
  <div>
    <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-3 sm:mb-4 flex items-center">
      {icon}
      <span className="ml-2">{title}</span>
    </h3>
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">{children}</div>
  </div>
);

const DataInput = ({ label, value, onChange }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
    <input type="number" inputMode="decimal" value={value} onChange={(e) => onChange(e.target.value)} step="0.01" min="0"
      className="w-full px-3 py-2.5 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" />
  </div>
);

// Empty shift form — used as both initial state and "Clear" reset target.
const EMPTY_SHIFT_FORM = {
  production_kgs: 0,
  despatches_kgs: 0,
  power_consumption_units: 0,
  steam_consumption_mt: 0,
  water_consumption_kl: 0,
  cabol_100_l: 0,
  freezol_68_l: 0,
  hydrol_100_l: 0,
  hydrol_150_l: 0,
  servo_320_l: 0,
  servo_220_l: 0,
  carbon_refill_kgs: 0,
  alumina_balls_refill_kgs: 0,
  mol_sieves_13x_kgs: 0,
  nh3_consumption_kgs: 0,
  kmno4_consumption_kgs: 0,
  maintenance_rs: 0,
  food_grade_expenses_rs: 0,
  running_mins: 0,
  pcpl_stoppage_mins: 0,
  source_stoppage_mins: 0,
  stoppage_reason: '',
};

const ShiftDataEntryView = ({ plants, selectedPlant, setSelectedPlant, token, user }) => {
  const today = new Date().toISOString().split('T')[0];
  const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];

  const [selectedDate, setSelectedDate] = useState(today);
  const [selectedShift, setSelectedShift] = useState('A');
  const [formData, setFormData] = useState(EMPTY_SHIFT_FORM);
  const [loading, setLoading] = useState(false);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestReason, setRequestReason] = useState('');
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [hasActiveApproval, setHasActiveApproval] = useState(false);

  const isDateOldForUser = selectedDate !== today && selectedDate !== yesterday && user.role !== 'admin';
  const isDateValid = !isDateOldForUser || hasActiveApproval;

  const checkEditApproval = useCallback(async () => {
    if (!isDateOldForUser || !selectedPlant) { setHasActiveApproval(false); return; }
    try {
      const res = await axios.get(`${API}/edit-requests?status_filter=approved`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const now = new Date();
      const match = res.data.find(r =>
        r.plant_id === selectedPlant.id &&
        r.date === selectedDate &&
        r.shift === selectedShift &&
        r.access_until && new Date(r.access_until) > now
      );
      setHasActiveApproval(!!match);
    } catch (_err) {
      setHasActiveApproval(false);
    }
  }, [isDateOldForUser, selectedPlant, selectedDate, selectedShift, token]);

  useEffect(() => { checkEditApproval(); }, [checkEditApproval]);

  const handleSubmitEditRequest = async () => {
    if (!requestReason.trim()) { alert('Please provide a reason'); return; }
    try {
      setSubmittingRequest(true);
      await axios.post(`${API}/edit-requests`, {
        plant_id: selectedPlant.id,
        date: selectedDate,
        shift: selectedShift,
        reason: requestReason.trim(),
      }, { headers: { Authorization: `Bearer ${token}` } });
      alert('Edit request submitted. An admin will review it shortly.');
      setShowRequestForm(false);
      setRequestReason('');
    } catch (error) {
      alert('Failed to submit: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setSubmittingRequest(false);
    }
  };
  
  const loadShiftData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/shifts/${selectedPlant.id}/${selectedDate}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const shiftData = response.data.find(s => s.shift === selectedShift);
      if (shiftData) {
        setFormData({
          production_kgs: shiftData.production_kgs,
          despatches_kgs: shiftData.despatches_kgs,
          power_consumption_units: shiftData.power_consumption_units,
          steam_consumption_mt: shiftData.steam_consumption_mt,
          water_consumption_kl: shiftData.water_consumption_kl,
          cabol_100_l: shiftData.cabol_100_l ?? 0,
          freezol_68_l: shiftData.freezol_68_l ?? 0,
          hydrol_100_l: shiftData.hydrol_100_l ?? 0,
          hydrol_150_l: shiftData.hydrol_150_l ?? 0,
          servo_320_l: shiftData.servo_320_l ?? 0,
          servo_220_l: shiftData.servo_220_l ?? 0,
          carbon_refill_kgs: shiftData.carbon_refill_kgs,
          alumina_balls_refill_kgs: shiftData.alumina_balls_refill_kgs,
          mol_sieves_13x_kgs: shiftData.mol_sieves_13x_kgs ?? 0,
          nh3_consumption_kgs: shiftData.nh3_consumption_kgs,
          kmno4_consumption_kgs: shiftData.kmno4_consumption_kgs,
          maintenance_rs: shiftData.maintenance_rs,
          food_grade_expenses_rs: shiftData.food_grade_expenses_rs,
          running_mins: shiftData.running_mins ?? 0,
          pcpl_stoppage_mins: shiftData.pcpl_stoppage_mins ?? 0,
          source_stoppage_mins: shiftData.source_stoppage_mins ?? 0,
          stoppage_reason: shiftData.stoppage_reason ?? '',
        });
      }
    } catch (error) {
      devLog.error('Failed to load shift data:', error);
    }
  }, [selectedPlant, selectedDate, selectedShift, token]);
  
  useEffect(() => {
    if (selectedPlant && selectedDate && selectedShift) {
      loadShiftData();
    }
  }, [selectedPlant, selectedDate, selectedShift, loadShiftData]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await axios.post(`${API}/shifts`, {
        plant_id: selectedPlant.id,
        date: selectedDate,
        shift: selectedShift,
        ...formData
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Shift data saved successfully!');
    } catch (error) {
      alert('Failed to save: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };
  
  const handleChange = (field, value) => {
    // Text fields keep their raw string value; numeric fields are parsed.
    const textFields = ['stoppage_reason'];
    const nextValue = textFields.includes(field) ? value : (parseFloat(value) || 0);
    setFormData(prev => ({ ...prev, [field]: nextValue }));
  };
  
  return (
    <div className="space-y-6">
      {selectedPlant && (
        <ExcelImportPanel
          title="Bulk Import / Export Shift Data (Excel)"
          description="Download the existing data to edit it, then re-upload with 'Overwrite existing' ticked (admins only). New rows are inserted; duplicate (date, shift) rows are skipped unless overwrite is on."
          templateUrl={`/plants/${selectedPlant.id}/shifts/template.xlsx`}
          importUrl={`/plants/${selectedPlant.id}/shifts/import`}
          templateFilename={`shift_data_template_${selectedPlant.name.replace(/\s+/g, '_')}.xlsx`}
          exportUrl={`/plants/${selectedPlant.id}/shifts/export.xlsx`}
          exportFilename={`shift_data_${selectedPlant.name.replace(/\s+/g, '_')}.xlsx`}
          token={token}
          testidPrefix="shift-bulk"
          allowOverwrite={user?.role === 'admin'}
        />
      )}
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Shift Data Entry</h2>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Plant</label>
          <select
            value={selectedPlant?.id || ''}
            onChange={(e) => setSelectedPlant(plants.find(p => p.id === e.target.value))}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
          >
            {plants.map(plant => (
              <option key={plant.id} value={plant.id}>{plant.name}</option>
            ))}
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
          <DateStepper
            value={selectedDate}
            onChange={setSelectedDate}
            maxDate={new Date()}
            testidPrefix="shift-date"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Shift</label>
          <select
            value={selectedShift}
            onChange={(e) => setSelectedShift(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
          >
            <option value="A">Shift A</option>
            <option value="B">Shift B</option>
            <option value="C">Shift C</option>
          </select>
        </div>
      </div>
      
      {isDateOldForUser && !hasActiveApproval && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-300 rounded-lg">
          <p className="text-yellow-900 text-sm font-medium mb-2">
            This date is older than yesterday. Non-admin users need admin approval to edit historic data.
          </p>
          {!showRequestForm ? (
            <button
              type="button"
              onClick={() => setShowRequestForm(true)}
              className="bg-[#156ACA] hover:bg-[#0F5299] text-white text-sm font-medium px-4 py-2 rounded-lg"
              data-testid="request-edit-access-btn"
            >
              Request edit access
            </button>
          ) : (
            <div className="mt-3 space-y-3" data-testid="edit-request-form">
              <textarea
                value={requestReason}
                onChange={(e) => setRequestReason(e.target.value)}
                placeholder="Reason for editing this historic shift data..."
                className="w-full p-3 border border-gray-300 rounded-lg text-sm"
                rows={3}
                data-testid="edit-request-reason"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleSubmitEditRequest}
                  disabled={submittingRequest}
                  className="bg-[#156ACA] hover:bg-[#0F5299] text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-50"
                  data-testid="submit-edit-request-btn"
                >
                  {submittingRequest ? 'Submitting...' : 'Submit request'}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowRequestForm(false); setRequestReason(''); }}
                  className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {isDateOldForUser && hasActiveApproval && (
        <div className="mb-6 p-4 bg-green-50 border border-green-300 rounded-lg">
          <p className="text-green-800 text-sm font-medium">
            Admin approval active for this plant / date / shift. You can edit this historic entry.
          </p>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <DataSection title="Production & Despatches" icon={<Package className="h-5 w-5" />}>
          <DataInput label="Production (Kgs)" value={formData.production_kgs} onChange={(v) => handleChange('production_kgs', v)} />
          <DataInput label="Despatches (Kgs)" value={formData.despatches_kgs} onChange={(v) => handleChange('despatches_kgs', v)} />
        </DataSection>
        
        <DataSection title="Consumption Parameters" icon={<TrendingUp className="h-5 w-5" />}>
          <DataInput label="Power (Units)" value={formData.power_consumption_units} onChange={(v) => handleChange('power_consumption_units', v)} />
          <DataInput label="Steam (MT)" value={formData.steam_consumption_mt} onChange={(v) => handleChange('steam_consumption_mt', v)} />
          <DataInput label="Water (KL)" value={formData.water_consumption_kl} onChange={(v) => handleChange('water_consumption_kl', v)} />
          <DataInput label="NH₃ (Kgs)" value={formData.nh3_consumption_kgs} onChange={(v) => handleChange('nh3_consumption_kgs', v)} />
          <DataInput label="KMNO₄ (Kgs)" value={formData.kmno4_consumption_kgs} onChange={(v) => handleChange('kmno4_consumption_kgs', v)} />
        </DataSection>

        <DataSection title="Oil Consumption (Litres)" icon={<TrendingUp className="h-5 w-5" />}>
          <DataInput label="Cabol 100 (L)" value={formData.cabol_100_l} onChange={(v) => handleChange('cabol_100_l', v)} />
          <DataInput label="Freezol-68 (L)" value={formData.freezol_68_l} onChange={(v) => handleChange('freezol_68_l', v)} />
          <DataInput label="Hydrol 100 (L)" value={formData.hydrol_100_l} onChange={(v) => handleChange('hydrol_100_l', v)} />
          <DataInput label="Hydrol 150 (L)" value={formData.hydrol_150_l} onChange={(v) => handleChange('hydrol_150_l', v)} />
          <DataInput label="Servo 320 (L)" value={formData.servo_320_l} onChange={(v) => handleChange('servo_320_l', v)} />
          <DataInput label="Servo 220 (L)" value={formData.servo_220_l} onChange={(v) => handleChange('servo_220_l', v)} />
        </DataSection>

        <DataSection title="Refills & Maintenance" icon={<IndianRupee className="h-5 w-5" />}>
          <DataInput label="Carbon Refill (Kgs)" value={formData.carbon_refill_kgs} onChange={(v) => handleChange('carbon_refill_kgs', v)} />
          <DataInput label="Alumina Balls (Kgs)" value={formData.alumina_balls_refill_kgs} onChange={(v) => handleChange('alumina_balls_refill_kgs', v)} />
          <DataInput label="13X Mol Sieves (Kgs)" value={formData.mol_sieves_13x_kgs} onChange={(v) => handleChange('mol_sieves_13x_kgs', v)} />
          <DataInput label="Maintenance (₹)" value={formData.maintenance_rs} onChange={(v) => handleChange('maintenance_rs', v)} />
          <DataInput label="Food Grade Expenses (₹)" value={formData.food_grade_expenses_rs} onChange={(v) => handleChange('food_grade_expenses_rs', v)} />
        </DataSection>

        <DataSection title="Plant Running & Stoppages" icon={<TrendingUp className="h-5 w-5" />}>
          <DataInput label="Plant Running (mins)" value={formData.running_mins} onChange={(v) => handleChange('running_mins', v)} />
          <DataInput label="PCPL Stoppage (mins)" value={formData.pcpl_stoppage_mins} onChange={(v) => handleChange('pcpl_stoppage_mins', v)} />
          <DataInput label="Source Stoppage (mins)" value={formData.source_stoppage_mins} onChange={(v) => handleChange('source_stoppage_mins', v)} />
          <div className="md:col-span-3">
            <label className="block text-sm font-medium text-gray-700 mb-2">Stoppage Reason</label>
            <input
              type="text"
              value={formData.stoppage_reason}
              onChange={(e) => handleChange('stoppage_reason', e.target.value)}
              placeholder="e.g. PCPL: power outage 20m; Source: feed valve replacement 10m"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              data-testid="stoppage-reason-input"
            />
          </div>
        </DataSection>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
          <button
            type="button"
            onClick={() => setFormData(EMPTY_SHIFT_FORM)}
            className="px-6 py-3 min-h-[44px] border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Clear Form
          </button>
          <button
            type="submit"
            disabled={loading || !isDateValid}
            className="bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 sm:px-8 min-h-[44px] rounded-lg disabled:opacity-50 flex items-center justify-center"
          >
            <Save className="h-5 w-5 mr-2" />
            {loading ? 'Saving...' : 'Save Shift Data'}
          </button>
        </div>
      </form>
      </div>
    </div>
  );
};

export default ShiftDataEntryView;
